/**
 * Raster analysis primitives for plan screenshots. Pure functions over typed
 * arrays; used inside the vision Web Worker. All coordinates are in "working"
 * pixels (the analysed image, possibly downscaled).
 */
import { closing } from '../render/liftcore.ts';

export interface Mask {
  w: number;
  h: number;
  data: Uint8Array; // 1 = set
}

export interface Component {
  id: number;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  count: number;
}

/** Luminance (Rec. 601) from RGBA. */
export function luminance(rgba: Uint8ClampedArray, n: number): Uint8Array {
  const out = new Uint8Array(n);
  for (let i = 0, j = 0; i < n; i++, j += 4) out[i] = (rgba[j]! * 77 + rgba[j + 1]! * 151 + rgba[j + 2]! * 28) >> 8;
  return out;
}

/** A plain ink threshold: a pixel darker than `t` is ink. */
export function inkMask(lum: Uint8Array, w: number, h: number, t = 215): Mask {
  const data = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) data[i] = lum[i]! < t ? 1 : 0;
  return { w, h, data };
}

/** Relative lightness below this (on paper at 255) is faint ink: a grey layer's lines, lighter than the ink threshold. */
const FAINT = 238;
/** A faint pixel this close (8-neighbour) to ink darker than this is the anti-aliased rim of that ink, not a line of its own. */
const RIM = 170;
/** Paper tone at or below this is a filled region of ink (a solid symbol), not shading. */
const DARK_TONE = 96;

/**
 * The plan's ink, read against the paper round it rather than against white.
 *
 * Each pixel's lightness is divided by the paper tone where it lies (a flat-field correction, as
 * the lift does for drawing: the lightness closed — a maximum then a minimum filter — over a window
 * wider than any line or letter), so shading, a tinted sheet and the black bars round a screenshot
 * become paper, while every line, letter and symbol keeps its darkness against it. A dark tone
 * inside the plan is a filled symbol, and stays ink; a dark tone that is a large area at the
 * picture's edge is the screen round the plan, and is not.
 *
 * Ink is what is darker than `t` against its paper, and also faint line work: a grey layer lighter
 * than `t` (to FAINT), except where it is only the soft rim of darker ink. `faint` marks those
 * pixels (1), so a line can be told by its tone as well as its weight.
 */
export function readInk(lum: Uint8Array, w: number, h: number, t = 215): { ink: Mask; faint: Uint8Array; paper: Uint8Array; grain: Uint8Array } {
  const n = w * h;
  // the window: wider than the heaviest line (≈ 1.6 % of the longest side: 33 px at 2048)
  const r = Math.max(4, Math.round(0.008 * Math.max(w, h)));
  const grain = grainOf(lum, w, h);
  const paper = closing(lum, w, h, r);
  const grainy = grain.some((g) => g >= GRAINY);
  if (grainy) {
    // grainy shading (a scan, a photograph of a sheet, a halftone): its brightest specks are not its
    // paper — the paper there is read from the plan smoothed a little, so it is the shading's own tone
    const smooth = closing(boxBlur(lum, w, h, 2), w, h, r);
    for (let i = 0; i < n; i++) {
      const g = grain[i]!;
      if (g <= GRAINY / 2) continue;
      const f = Math.min(1, (g - GRAINY / 2) / (GRAINY / 2));
      paper[i] = Math.round(paper[i]! * (1 - f) + smooth[i]! * f);
    }
  }
  // dark tone: a filled symbol (ink on paper) unless it is a large area at the edge (the screen round the plan)
  const dark: Mask = { w, h, data: new Uint8Array(n) };
  for (let i = 0; i < n; i++) dark.data[i] = paper[i]! <= DARK_TONE ? 1 : 0;
  const { labels, comps } = labelComponents(dark, false);
  const outside = new Uint8Array(comps.length + 1);
  for (const c of comps) outside[c.id] = c.count >= 0.01 * n && (c.minX === 0 || c.minY === 0 || c.maxX === w - 1 || c.maxY === h - 1) ? 1 : 0;
  const rel = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    if (dark.data[i]) {
      // a filled symbol is ink against white; the screen round the plan is never ink
      if (outside[labels[i]!]) rel[i] = 255;
      else {
        paper[i] = 255;
        rel[i] = lum[i]!;
      }
      continue;
    }
    const p = paper[i]!;
    if (p >= 250) rel[i] = lum[i]!;
    else {
      const v = (lum[i]! * INV[p]! + 32768) >> 16;
      rel[i] = v > 255 ? 255 : v;
    }
  }
  // (and ink is darker than its paper by more than the paper's own grain)
  const clear = (i: number) => paper[i]! - lum[i]! >= grain[i]!;
  const data = new Uint8Array(n);
  const faint = new Uint8Array(n);
  for (let i = 0; i < n; i++) if (rel[i]! < t && clear(i)) data[i] = 1;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      if (data[i] || rel[i]! >= FAINT || !clear(i)) continue;
      // the rim of darker ink (anti-aliasing, JPEG ringing) is not a line of its own
      if (rel[i - w - 1]! < RIM || rel[i - w]! < RIM || rel[i - w + 1]! < RIM || rel[i - 1]! < RIM || rel[i + 1]! < RIM || rel[i + w - 1]! < RIM || rel[i + w]! < RIM || rel[i + w + 1]! < RIM) continue;
      faint[i] = 1;
    }
  }
  // faint specks are noise: a faint pixel stays only in a run of faint ink that makes a line
  const fm: Mask = { w, h, data: faint };
  const fl = labelComponents(fm, true);
  const keep = new Uint8Array(fl.comps.length + 1);
  for (const c of fl.comps) {
    const len = Math.hypot(c.maxX - c.minX + 1, c.maxY - c.minY + 1);
    keep[c.id] = len >= 8 && c.count >= 6 ? 1 : 0;
  }
  for (let i = 0; i < n; i++) {
    if (!faint[i]) continue;
    if (keep[fl.labels[i]!]) data[i] = 1;
    else faint[i] = 0;
  }
  if (grainy) {
    // in grainy shading what is left of the grain is specks: a mark there must be more than a speck
    const { labels: gl, comps: gc } = labelComponents({ w, h, data }, true);
    const drop = new Uint8Array(gc.length + 1);
    for (const c of gc) {
      if (c.count >= SPECK) continue;
      const g = grain[Math.round((c.minY + c.maxY) / 2) * w + Math.round((c.minX + c.maxX) / 2)]!;
      if (g >= GRAINY) drop[c.id] = 1;
    }
    for (let i = 0; i < n; i++)
      if (data[i] && drop[gl[i]!]) {
        data[i] = 0;
        faint[i] = 0;
      }
  }
  return { ink: { w, h, data }, faint, paper, grain };
}

/** 255 / p in 16-bit fixed point, for every paper tone p. */
const INV = (() => {
  const t = new Int32Array(256);
  for (let p = 1; p < 256; p++) t[p] = Math.round((255 * 65536) / p);
  return t;
})();

/** Grain this strong (levels a pixel must stand below its paper to be ink) marks grainy shading. */
const GRAINY = 12;
/** In grainy shading, a mark of fewer pixels than this is a speck of the grain. */
const SPECK = 16;

/**
 * The paper's grain round each pixel: how much darker than its paper a pixel must be before it
 * can be told from the grain (levels of lightness; 0 on clean paper). Measured per block of 32 px
 * on its paper-toned pixels alone (its lightest 40 %: on clean paper flat, however much line work
 * the block holds; in grainy shading as speckled as the rest) — how much lighter each one's
 * lightest neighbour is, the median of those — each pixel taking the grainiest block about it.
 */
export function grainOf(lum: Uint8Array, w: number, h: number): Uint8Array {
  const B = 32;
  const bx = Math.ceil(w / B),
    by = Math.ceil(h / B);
  // (every other pixel each way is plenty to know a block's grain by)
  const med = new Float32Array(bx * by);
  const hl = new Int32Array(256),
    hd = new Int32Array(256);
  for (let j = 0; j < by; j++)
    for (let i = 0; i < bx; i++) {
      const x0 = Math.max(1, i * B),
        x1 = Math.min(w - 1, i * B + B),
        y0 = Math.max(1, j * B),
        y1 = Math.min(h - 1, j * B + B);
      hl.fill(0);
      hd.fill(0);
      let cnt = 0;
      for (let y = y0; y < y1; y += 2) for (let x = x0; x < x1; x += 2) hl[lum[y * w + x]!]!++, cnt++;
      if (!cnt) continue;
      let acc = 0,
        L = 0;
      for (; L < 255; L++) if ((acc += hl[L]!) >= 0.6 * cnt) break;
      // each paper-toned pixel against its lightest neighbour: in the gap between two hatch lines
      // the gap runs on, as light, so it is 0 there as on clean paper; in grain some neighbour is
      // always lighter
      let m = 0;
      for (let y = y0; y < y1; y += 2)
        for (let x = x0; x < x1; x += 2) {
          const k = y * w + x;
          const v = lum[k]!;
          if (v < L) continue;
          let n = lum[k - w - 1]!;
          if (lum[k - w]! > n) n = lum[k - w]!;
          if (lum[k - w + 1]! > n) n = lum[k - w + 1]!;
          if (lum[k - 1]! > n) n = lum[k - 1]!;
          if (lum[k + 1]! > n) n = lum[k + 1]!;
          if (lum[k + w - 1]! > n) n = lum[k + w - 1]!;
          if (lum[k + w]! > n) n = lum[k + w]!;
          if (lum[k + w + 1]! > n) n = lum[k + w + 1]!;
          hd[n > v ? n - v : 0]!++;
          m++;
        }
      acc = 0;
      let d = 0;
      for (; d < 255; d++) if ((acc += hd[d]!) >= 0.5 * m) break;
      // (some twice the grain's own spread: what is left of it is specks, cleared by size)
      med[j * bx + i] = 6 * d;
    }
  // grain is shading's, and shading covers some blocks together: a block reading grainy among
  // blocks that do not (the busy middle of a symbol, a knot of hatching) is not — the median of
  // each block's 3×3, then carried back out to a grainy region's edge blocks (never above their own)
  const around = (src: Float32Array, pick: (v: number[]) => number) => {
    const o = new Float32Array(bx * by);
    for (let j = 0; j < by; j++)
      for (let i = 0; i < bx; i++) {
        const v: number[] = [];
        for (let dj = -1; dj <= 1; dj++)
          for (let di = -1; di <= 1; di++) {
            const jj = j + dj,
              ii = i + di;
            if (jj >= 0 && ii >= 0 && jj < by && ii < bx) v.push(src[jj * bx + ii]!);
          }
        o[j * bx + i] = pick(v);
      }
    return o;
  };
  const mid = around(med, (v) => v.sort((a, b) => a - b)[v.length >> 1]!);
  const grown = around(mid, (v) => Math.max(...v));
  let any = false;
  for (let i = 0; i < bx * by; i++) {
    med[i] = Math.min(med[i]!, grown[i]!);
    if (med[i]) any = true;
  }
  // each pixel given its blocks' (clean paper throughout: none)
  const out = new Uint8Array(w * h);
  if (!any) return out;
  for (let y = 0; y < h; y++) {
    const fy = Math.max(0, Math.min(by - 1, (y + 0.5) / B - 0.5));
    const j0 = Math.floor(fy),
      j1 = Math.min(by - 1, j0 + 1);
    for (let x = 0; x < w; x++) {
      const fx = Math.max(0, Math.min(bx - 1, (x + 0.5) / B - 0.5));
      const i0 = Math.floor(fx),
        i1 = Math.min(bx - 1, i0 + 1);
      // (the grain of the grainiest block about it: shading's grain runs right up to its edge, and a
      // block half shading, half white paper reads its white half)
      const v = Math.max(med[j0 * bx + i0]!, med[j0 * bx + i1]!, med[j1 * bx + i0]!, med[j1 * bx + i1]!);
      out[y * w + x] = v > 255 ? 255 : v;
    }
  }
  return out;
}

/** A box blur of radius r (separable, running sums; the edges averaged over what lies inside). */
export function boxBlur(src: Uint8Array, w: number, h: number, r: number): Uint8Array {
  const tmp = new Uint8Array(w * h),
    out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    const o = y * w;
    let s = 0,
      c = 0;
    for (let x = 0; x <= Math.min(w - 1, r); x++) (s += src[o + x]!), c++;
    for (let x = 0; x < w; x++) {
      tmp[o + x] = Math.round(s / c);
      const add = x + r + 1,
        sub = x - r;
      if (add < w) (s += src[o + add]!), c++;
      if (sub >= 0) (s -= src[o + sub]!), c--;
    }
  }
  for (let x = 0; x < w; x++) {
    let s = 0,
      c = 0;
    for (let y = 0; y <= Math.min(h - 1, r); y++) (s += tmp[y * w + x]!), c++;
    for (let y = 0; y < h; y++) {
      out[y * w + x] = Math.round(s / c);
      const add = y + r + 1,
        sub = y - r;
      if (add < h) (s += tmp[add * w + x]!), c++;
      if (sub >= 0) (s -= tmp[sub * w + x]!), c--;
    }
  }
  return out;
}

/** 8-connected component labelling (BFS). Returns label map (0 = none) and stats. */
export function labelComponents(mask: Mask, conn8 = true): { labels: Int32Array; comps: Component[] } {
  const { w, h, data } = mask;
  const labels = new Int32Array(w * h);
  const comps: Component[] = [];
  const queue = new Int32Array(w * h);
  let next = 1;
  for (let start = 0; start < w * h; start++) {
    if (!data[start] || labels[start]) continue;
    const id = next++;
    let head = 0,
      tail = 0;
    queue[tail++] = start;
    labels[start] = id;
    let minX = w,
      minY = h,
      maxX = -1,
      maxY = -1,
      count = 0;
    while (head < tail) {
      const p = queue[head++]!;
      const x = p % w,
        y = (p - x) / w;
      count++;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      const x0 = x > 0,
        x1 = x < w - 1,
        y0 = y > 0,
        y1 = y < h - 1;
      let q: number;
      if (x0 && data[(q = p - 1)] && !labels[q]) {
        labels[q] = id;
        queue[tail++] = q;
      }
      if (x1 && data[(q = p + 1)] && !labels[q]) {
        labels[q] = id;
        queue[tail++] = q;
      }
      if (y0 && data[(q = p - w)] && !labels[q]) {
        labels[q] = id;
        queue[tail++] = q;
      }
      if (y1 && data[(q = p + w)] && !labels[q]) {
        labels[q] = id;
        queue[tail++] = q;
      }
      if (conn8) {
        if (x0 && y0 && data[(q = p - w - 1)] && !labels[q]) {
          labels[q] = id;
          queue[tail++] = q;
        }
        if (x1 && y0 && data[(q = p - w + 1)] && !labels[q]) {
          labels[q] = id;
          queue[tail++] = q;
        }
        if (x0 && y1 && data[(q = p + w - 1)] && !labels[q]) {
          labels[q] = id;
          queue[tail++] = q;
        }
        if (x1 && y1 && data[(q = p + w + 1)] && !labels[q]) {
          labels[q] = id;
          queue[tail++] = q;
        }
      }
    }
    comps.push({ id, minX, minY, maxX, maxY, count });
  }
  return { labels, comps };
}

/**
 * Text detection: glyph-like components (compact, small, not thin bars) that
 * line up in rows. Returns the set of component ids that are text, and their
 * row boxes. Everything inside a text row box that is small is also text
 * (thin glyphs like "1" and "l", punctuation).
 */
export function detectText(comps: Component[], maxGlyph: number): { text: Set<number>; rows: { minX: number; minY: number; maxX: number; maxY: number }[] } {
  const glyphs = comps.filter((c) => {
    const bw = c.maxX - c.minX + 1;
    const bh = c.maxY - c.minY + 1;
    if (bw > maxGlyph || bh > maxGlyph) return false;
    if (bh < 5 || bw < 3) return false;
    if (Math.min(bw, bh) < 4) return false; // thin bars: dashes, ticks, "1"
    if (bw > 1.6 * bh) return false; // letters are never this wide: a dash with a tick, a symbol
    const fill = c.count / (bw * bh);
    return fill > 0.12 && fill < 0.7; // solid squares/dots are symbols or dash-dots, not letters
  });
  glyphs.sort((a, b) => a.minY - b.minY || a.minX - b.minX);
  const text = new Set<number>();
  const rows: { minX: number; minY: number; maxX: number; maxY: number }[] = [];
  const used = new Uint8Array(glyphs.length);
  for (let i = 0; i < glyphs.length; i++) {
    if (used[i]) continue;
    const row = [glyphs[i]!];
    used[i] = 1;
    let changed = true;
    while (changed) {
      changed = false;
      const minY = Math.min(...row.map((g) => g.minY));
      const maxY = Math.max(...row.map((g) => g.maxY));
      const rh = maxY - minY + 1;
      const minX = Math.min(...row.map((g) => g.minX));
      const maxX = Math.max(...row.map((g) => g.maxX));
      for (let j = 0; j < glyphs.length; j++) {
        if (used[j]) continue;
        const g = glyphs[j]!;
        const gh = g.maxY - g.minY + 1;
        const overlapY = Math.min(maxY, g.maxY) - Math.max(minY, g.minY) + 1;
        if (overlapY < Math.min(rh, gh) * 0.5) continue;
        if (gh > rh * 1.8 || rh > gh * 1.8) continue;
        const gap = g.minX > maxX ? g.minX - maxX : g.maxX < minX ? minX - g.maxX : 0;
        if (gap > rh * 1.6) continue;
        row.push(g);
        used[j] = 1;
        changed = true;
      }
    }
    const rowH = Math.max(...row.map((g) => g.maxY - g.minY + 1));
    // Small text: two glyphs in a row is enough. Big text (titles) needs three so pairs of symbols survive.
    if (row.length >= (rowH > 40 ? 3 : 2)) {
      for (const g of row) text.add(g.id);
      rows.push({ minX: Math.min(...row.map((g) => g.minX)), minY: Math.min(...row.map((g) => g.minY)), maxX: Math.max(...row.map((g) => g.maxX)), maxY: Math.max(...row.map((g) => g.maxY)) });
    }
  }
  // Small components (thin glyphs, punctuation) fully inside a row box are text too.
  for (const c of comps) {
    if (text.has(c.id)) continue;
    const bw = c.maxX - c.minX + 1,
      bh = c.maxY - c.minY + 1;
    if (bw > maxGlyph || bh > maxGlyph) continue;
    for (const r of rows) {
      if (c.minX >= r.minX - 2 && c.maxX <= r.maxX + 2 && c.minY >= r.minY - 2 && c.maxY <= r.maxY + 2) {
        text.add(c.id);
        break;
      }
    }
  }
  return { text, rows };
}

/** Mask without the given components (and without dust smaller than minCount). */
export function withoutComponents(mask: Mask, labels: Int32Array, drop: Set<number>, comps: Component[], minCount: number): Mask {
  const small = new Set<number>();
  for (const c of comps) if (c.count < minCount) small.add(c.id);
  const data = new Uint8Array(mask.w * mask.h);
  for (let i = 0; i < data.length; i++) {
    const l = labels[i]!;
    data[i] = mask.data[i] && !drop.has(l) && !small.has(l) ? 1 : 0;
  }
  return { w: mask.w, h: mask.h, data };
}

/**
 * The squared distance from ink pixel i to the nearest pixel that is not ink,
 * found by looking outward from it ring by ring — cheap where the ink is a line, as it mostly is
 * (as `distanceTransform` of the background gives, for the few pixels it is wanted at).
 */
export function distanceOut(mask: Mask, i: number): number {
  const { w, h, data } = mask;
  if (!data[i]) return 0;
  const x0 = i % w,
    y0 = (i - x0) / w;
  let best = Infinity;
  for (let r = 1; ; r++) {
    // every pixel on the square ring at Chebyshev distance r (off the raster is not paper)
    if (r * r >= best || r > w + h) return best;
    for (let dy = -r; dy <= r; dy++) {
      const y = y0 + dy;
      const step = dy === -r || dy === r ? 1 : 2 * r;
      for (let dx = -r; dx <= r; dx += step) {
        const x = x0 + dx;
        const d = dx * dx + dy * dy;
        if (d >= best) continue;
        if (x >= 0 && y >= 0 && x < w && y < h && !data[y * w + x]) best = d;
      }
    }
  }
}

/** Each pixel's distance to the nearest line ink (working px, rounded, to 63): the walls of the plan's regions (faces.ts). */
export function inkDistances(lineMask: Uint8Array, cw: number, ch: number): Uint8Array {
  const dtInk = distanceTransform({ w: cw, h: ch, data: lineMask });
  const edt = new Uint8Array(cw * ch);
  for (let i = 0; i < edt.length; i++) edt[i] = Math.min(63, Math.round(Math.sqrt(dtInk[i]!)));
  return edt;
}

/**
 * Exact Euclidean distance transform (Felzenszwalb & Huttenlocher): distance
 * from every pixel to the nearest set pixel of the mask. Returns squared
 * distances as Float32.
 */
export function distanceTransform(mask: Mask): Float32Array {
  const { w, h, data } = mask;
  const INF = 1e12;
  const f = new Float32Array(Math.max(w, h));
  const d = new Float32Array(Math.max(w, h));
  const v = new Int32Array(Math.max(w, h));
  const z = new Float32Array(Math.max(w, h) + 1);
  const out = new Float32Array(w * h);
  const dt1d = (n: number) => {
    let k = 0;
    v[0] = 0;
    z[0] = -INF;
    z[1] = INF;
    for (let q = 1; q < n; q++) {
      let s = (f[q]! + q * q - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!);
      while (s <= z[k]!) {
        k--;
        s = (f[q]! + q * q - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!);
      }
      k++;
      v[k] = q;
      z[k] = s;
      z[k + 1] = INF;
    }
    k = 0;
    for (let q = 0; q < n; q++) {
      while (z[k + 1]! < q) k++;
      d[q] = (q - v[k]!) * (q - v[k]!) + f[v[k]!]!;
    }
  };
  // columns
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = data[y * w + x] ? 0 : INF;
    dt1d(h);
    for (let y = 0; y < h; y++) out[y * w + x] = d[y]!;
  }
  // rows
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) f[x] = out[y * w + x]!;
    dt1d(w);
    for (let x = 0; x < w; x++) out[y * w + x] = d[x]!;
  }
  return out;
}

/**
 * Zhang–Suen thinning. Returns a new one-pixel-wide skeleton mask. Only the ink still standing is
 * visited on each pass (a plan is mostly paper), so it costs in proportion to the ink, not the raster;
 * the result is the same as the classic whole-raster passes.
 */
export function thin(mask: Mask): Mask {
  const { w, h } = mask;
  const img = new Uint8Array(mask.data);
  // A pixel goes or stays by its eight neighbours alone, so each sub-pass looks only at the pixels
  // whose neighbours have changed since that sub-pass last looked at them: at first every pixel on
  // the ink's edge (one ringed all round by ink never goes), then those beside a pixel just cleared.
  // The frame is never thinned (as in the classic form): only pixels with all neighbours inside the raster
  const inner = (i: number) => {
    const x = i % w;
    return x > 0 && x < w - 1 && i >= w && i < w * (h - 1);
  };
  const flags = [new Uint8Array(img.length), new Uint8Array(img.length)] as const;
  const lists = [new Int32Array(1024), new Int32Array(1024)];
  const lens = [0, 0];
  const push = (pass: number, i: number) => {
    if (flags[pass]![i]) return;
    flags[pass]![i] = 1;
    let l = lists[pass]!;
    if (lens[pass] === l.length) {
      const next = new Int32Array(l.length * 2);
      next.set(l);
      lists[pass] = l = next;
    }
    l[lens[pass]!++] = i;
  };
  for (let y = 1; y < h - 1; y++)
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      if (!img[i]) continue;
      if (img[i - w - 1]! & img[i - w]! & img[i - w + 1]! & img[i - 1]! & img[i + 1]! & img[i + w - 1]! & img[i + w]! & img[i + w + 1]!) continue;
      push(0, i);
      push(1, i);
    }
  let toClear = new Int32Array(1024);
  const n8 = [-w - 1, -w, -w + 1, -1, 1, w - 1, w, w + 1];
  for (;;) {
    let changed = false;
    for (let pass = 0; pass < 2; pass++) {
      const lut = pass === 0 ? ZS0 : ZS1;
      const list = lists[pass]!,
        flag = flags[pass]!;
      const n = lens[pass]!;
      let c = 0;
      for (let j = 0; j < n; j++) {
        const i = list[j]!;
        flag[i] = 0;
        if (!img[i]) continue;
        const code = img[i - w]! | (img[i - w + 1]! << 1) | (img[i + 1]! << 2) | (img[i + w + 1]! << 3) | (img[i + w]! << 4) | (img[i + w - 1]! << 5) | (img[i - 1]! << 6) | (img[i - w - 1]! << 7);
        if (!lut[code]) continue;
        if (c === toClear.length) {
          const next = new Int32Array(toClear.length * 2);
          next.set(toClear);
          toClear = next;
        }
        toClear[c++] = i;
      }
      lens[pass] = 0;
      if (!c) continue;
      changed = true;
      for (let j = 0; j < c; j++) img[toClear[j]!] = 0;
      // what is beside a cleared pixel is looked at again, in both sub-passes
      for (let j = 0; j < c; j++) {
        const i = toClear[j]!;
        for (const d of n8) {
          const q = i + d;
          if (img[q] && inner(q)) {
            push(0, q);
            push(1, q);
          }
        }
      }
    }
    if (!changed) break;
  }
  return { w, h, data: img };
}

/** Zhang–Suen: whether a pixel with neighbours `code` (bit 0 N, then clockwise to bit 7 NW) goes, in each sub-pass. */
const [ZS0, ZS1] = (() => {
  const a = new Uint8Array(256),
    b = new Uint8Array(256);
  for (let code = 0; code < 256; code++) {
    const p = (k: number) => (code >> k) & 1;
    const p2 = p(0),
      p3 = p(1),
      p4 = p(2),
      p5 = p(3),
      p6 = p(4),
      p7 = p(5),
      p8 = p(6),
      p9 = p(7);
    const B = p2 + p3 + p4 + p5 + p6 + p7 + p8 + p9;
    if (B < 2 || B > 6) continue;
    const seq = [p2, p3, p4, p5, p6, p7, p8, p9, p2];
    let A = 0;
    for (let k = 0; k < 8; k++) if (seq[k] === 0 && seq[k + 1] === 1) A++;
    if (A !== 1) continue;
    if (p2 * p4 * p6 === 0 && p4 * p6 * p8 === 0) a[code] = 1;
    if (p2 * p4 * p8 === 0 && p2 * p6 * p8 === 0) b[code] = 1;
  }
  return [a, b];
})();

/**
 * Thinning wears a small enough mark (a dot two pixels across, a stub) away altogether: every
 * mark of the ink keeps at least one pixel of skeleton — the one nearest its middle. (The ink the
 * skeleton reaches is spread through first: only the marks it never reaches are looked at.)
 */
export function keepEveryMark(ink: Mask, skel: Mask): void {
  const { w, h, data } = ink;
  const reached = new Uint8Array(w * h);
  const queue = new Int32Array(w * h);
  let tail = 0;
  for (let i = 0; i < w * h; i++)
    if (skel.data[i] && data[i]) {
      reached[i] = 1;
      queue[tail++] = i;
    }
  const spread = (head: number): number => {
    while (head < tail) {
      const p = queue[head++]!;
      const x = p % w,
        y = (p - x) / w;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= w) continue;
          const q = yy * w + xx;
          if (data[q] && !reached[q]) {
            reached[q] = 1;
            queue[tail++] = q;
          }
        }
      }
    }
    return head;
  };
  spread(0);
  for (let s = 0; s < w * h; s++) {
    if (!data[s] || reached[s]) continue;
    // a mark the skeleton lost: its pixel nearest its box's middle (the first such, in reading order)
    const from = tail;
    reached[s] = 1;
    queue[tail++] = s;
    spread(from);
    let minX = w,
      minY = h,
      maxX = -1,
      maxY = -1;
    for (let j = from; j < tail; j++) {
      const p = queue[j]!;
      const x = p % w,
        y = (p - x) / w;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    const cx = (minX + maxX) / 2,
      cy = (minY + maxY) / 2;
    let best = -1,
      bd = Infinity;
    for (let j = from; j < tail; j++) {
      const p = queue[j]!;
      const x = p % w,
        y = (p - x) / w;
      const d = (x - cx) ** 2 + (y - cy) ** 2;
      if (d < bd || (d === bd && p < best)) {
        bd = d;
        best = p;
      }
    }
    if (best >= 0) skel.data[best] = 1;
  }
}

/**
 * For each pattern of a pixel's 8 neighbours (bit i: N, NE, E, SE, S, SW, W, NW), the number of
 * separate groups those neighbours make on their own (8-connected among themselves).
 */
const GROUPS = (() => {
  const t = new Uint8Array(256);
  for (let m = 0; m < 256; m++) {
    const parent = [0, 1, 2, 3, 4, 5, 6, 7];
    const find = (a: number): number => (parent[a] === a ? a : (parent[a] = find(parent[a]!)));
    const join = (a: number, b: number) => {
      if (m & (1 << a) && m & (1 << b)) parent[find(a)] = find(b);
    };
    for (let i = 0; i < 8; i++) {
      join(i, (i + 1) % 8); // ring neighbours touch
      if (i % 2 === 0) join(i, (i + 2) % 8); // two edge neighbours (N and E, …) touch at a corner
    }
    const roots = new Set<number>();
    for (let i = 0; i < 8; i++) if (m & (1 << i)) roots.add(find(i));
    t[m] = roots.size;
  }
  return t;
})();

/**
 * Make a thinned skeleton minimal: take out every pixel the line does not need — one whose
 * neighbours stay joined without it (a stair step where a slanting line turns a corner, the
 * clump at a junction). Zhang–Suen leaves such steps along lines at some slants, and each one
 * reads as a junction: whole stretches of a line would come apart into junction clumps.
 * An end of a line (a single neighbour) always stays.
 */
export function minimalSkeleton(skel: Mask): void {
  const { w, h, data } = skel;
  // the picture's own edge is left unthinned by the thinning; a line reaching it ends one pixel in
  // (so every line has an end, and nothing is read across the edge)
  for (let x = 0; x < w; x++) data[x] = data[(h - 1) * w + x] = 0;
  for (let y = 0; y < h; y++) data[y * w] = data[y * w + w - 1] = 0;
  for (let pass = 0; pass < 2; pass++) {
    let removed = 0;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = y * w + x;
        if (!data[i]) continue;
        const m = data[i - w]! | (data[i - w + 1]! << 1) | (data[i + 1]! << 2) | (data[i + w + 1]! << 3) | (data[i + w]! << 4) | (data[i + w - 1]! << 5) | (data[i - 1]! << 6) | (data[i - w - 1]! << 7);
        if (GROUPS[m] !== 1) continue; // a bridge between parts, or alone
        let c = 0;
        for (let b = m; b; b &= b - 1) c++;
        if (c < 2) continue; // an end of a line
        // two neighbours side by side (N and NE) are a line's end with a burr, not a step: keep the end
        if (c === 2 && (m === 0b11 || m === 0b110 || m === 0b1100 || m === 0b11000 || m === 0b110000 || m === 0b1100000 || m === 0b11000000 || m === 0b10000001)) continue;
        data[i] = 0;
        removed++;
      }
    }
    if (!removed) break;
  }
}

/** Number of 8-neighbours set. */
export function neighbours8(img: Uint8Array, w: number, i: number): number {
  return img[i - w - 1]! + img[i - w]! + img[i - w + 1]! + img[i - 1]! + img[i + 1]! + img[i + w - 1]! + img[i + w]! + img[i + w + 1]!;
}

/** Remove skeleton spurs shorter than minLen pixels (iteratively from endpoints). */
export function pruneSpurs(skel: Mask, minLen: number): void {
  const { w, h, data } = skel;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      if (!data[i] || neighbours8(data, w, i) !== 1) continue;
      // walk the spur
      const path: number[] = [i];
      let cur = i;
      let prev = -1;
      let ok = true;
      for (let step = 0; step < minLen; step++) {
        let nxt = -1;
        let deg = 0;
        for (const d of [-w - 1, -w, -w + 1, -1, 1, w - 1, w, w + 1]) {
          const q = cur + d;
          if (data[q] && q !== prev) {
            deg++;
            nxt = q;
          }
        }
        if (deg === 0) {
          ok = false; // isolated short line: leave it
          break;
        }
        if (deg > 1) break; // reached a junction: spur ends here
        prev = cur;
        cur = nxt;
        path.push(cur);
        if (path.length >= minLen) {
          ok = false;
          break;
        }
      }
      if (ok && path.length < minLen) {
        for (let k = 0; k < path.length - 1; k++) data[path[k]!] = 0;
      }
    }
  }
}

/** Downscale RGBA by an integer-free scale using canvas is done by the caller; this is a box filter for masks. */
export function downsampleMask(mask: Mask, factor: number): Mask {
  const w = Math.max(1, Math.floor(mask.w / factor));
  const h = Math.max(1, Math.floor(mask.h / factor));
  const data = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let any = 0;
      for (let dy = 0; dy < factor && !any; dy++) for (let dx = 0; dx < factor; dx++) if (mask.data[(y * factor + dy) * mask.w + x * factor + dx]) {
        any = 1;
        break;
      }
      data[y * w + x] = any;
    }
  }
  return { w, h, data };
}
